/* eslint-disable */
import type { JobSalary, Quantile } from "@lossdog/job-watchlists";
import { Typography } from "@povio/ui";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Area, CartesianGrid, ComposedChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { Button } from "@/components/ui/buttons/Button/Button";
import { Loader } from "@/components/ui/status/Loader/Loader";
import {
  toHistoricSalaryDataPoint,
  useHistoricalSalaryData,
} from "@/components/watchlist/hooks/useHistoricalSalaryData";
import { NumberUtils } from "@/util/number.utils";

const CHART_HEIGHT = 300;

const GRID_TICK = 5_000;

export interface HistoricalSalaryDataPoint {
  rawDate: Date;
  date: string;
  baseSalary: number;
}

export type TimePeriod = "ytd" | "five-year";

interface HistoricalSalaryChartProps {
  jobData: JobSalary | null;
  quantile: Quantile | null;
  timePeriod?: TimePeriod;
}

// Custom tooltip to show formatted salary values - styled to match UI Tooltip component
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="max-w-64 rounded-xs bg-secondary-200 px-3 py-2 shadow-2">
        <Typography
          size="label-2"
          className="mb-2 text-text-default-3"
        >
          {`Date: ${label}`}
        </Typography>
        <div className="space-y-1">
          {payload.map((entry: any, index: number) => {
            return (
              <Typography
                key={index}
                size="label-2"
                className="text-text-default-3"
              >
                {`${entry.name}: ${NumberUtils.formatAsUSCurrency(entry.value)}`}
              </Typography>
            );
          })}
        </div>
      </div>
    );
  }
  return null;
};

// Format Y-axis values to show as currency
const formatYAxisValue = (value: number) => {
  return `${NumberUtils.formatAsUSCurrency(value / 1_000)}k`;
};

// Custom X-axis tick component with Typography
const CustomXAxisTick = (props: any) => {
  const { x, y, payload } = props;
  return (
    <g transform={`translate(${x},${y})`}>
      <foreignObject
        x={-50}
        y={0}
        width={100}
        height={20}
      >
        <div className="flex justify-center">
          <Typography
            size="label-3"
            className="text-text-default-3"
          >
            {payload.value}
          </Typography>
        </div>
      </foreignObject>
    </g>
  );
};

// Custom Y-axis tick component with Typography
const CustomYAxisTick = (props: any) => {
  const { x, y, payload } = props;
  return (
    <g transform={`translate(${x},${y})`}>
      <foreignObject
        x={-60}
        y={-10}
        width={55}
        height={20}
      >
        <div className="flex justify-end">
          <Typography
            size="label-3"
            className="text-text-default-3"
          >
            {formatYAxisValue(payload.value)}
          </Typography>
        </div>
      </foreignObject>
    </g>
  );
};

const calculateYAxisDomain = (
  minSalary: number,
  maxSalary: number,
  data: HistoricalSalaryDataPoint[],
): [number, number] => {
  if (!data.length) return [0, 250_000];

  const roundedMin = Math.floor(minSalary / GRID_TICK) * GRID_TICK;

  // Round up the maximum to the nearest 25000 for better tick distribution
  const roundedMax = Math.ceil(maxSalary / GRID_TICK) * GRID_TICK;

  return [roundedMin, roundedMax];
};

export const HistoricalSalaryChart: React.FC<HistoricalSalaryChartProps> = ({
  jobData,
  quantile,
  timePeriod = "five-year",
}) => {
  const { t } = useTranslation();

  // Use the hook to fetch real data when jobData is provided
  const {
    data: historicalData,
    minSalary,
    maxSalary,
    isLoading,
    error,
    refetch,
  } = useHistoricalSalaryData({
    jobSalary: jobData,
    quantile,
    timePeriod,
  });

  const [liveData, setLiveData] = React.useState<HistoricalSalaryDataPoint[]>([
    ...historicalData,
    toHistoricSalaryDataPoint(jobData),
  ]);
  const [yAxisDomain, setYAxisDomain] = React.useState<[number, number]>([0, 250_000]);

  useEffect(() => {
    // Calculate Y-axis domain based on data
    setYAxisDomain(calculateYAxisDomain(minSalary, maxSalary, historicalData));
  }, [minSalary, maxSalary, historicalData]);

  // HACK: mixing React state with Preact Signals causes too many re-renders
  // Instead, use interval to mimic live chart data
  useEffect(() => {
    setLiveData([...historicalData, toHistoricSalaryDataPoint(jobData)]);

    let prevValue = jobData?.salary.value ?? 0;
    const intervalId = setInterval(() => {
      const currentValue = jobData?.salary.value ?? 0;
      if (currentValue === prevValue) {
        return;
      }
      prevValue = currentValue;
      setLiveData([...historicalData, toHistoricSalaryDataPoint(jobData)]);
    }, 1_000);
    return () => clearInterval(intervalId);
  }, [historicalData, jobData]);

  const hasError = jobData && error;

  // Show loading state
  if (jobData && isLoading) {
    return (
      <div className="flex h-[300px] w-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="w-full px-4">
      <div className="flex flex-row items-center justify-between py-2">
        <Typography
          size="label-4"
          className="text-text-default-3"
        >
          {t("jobDetails.historicalSalaryTrend.title")}
        </Typography>
        <div className="flex">
          <Typography
            size="label-2"
            variant="prominent-1"
            className="text-text-default-1"
          >
            {timePeriod === "five-year" ? "5Y" : "1Y"}
          </Typography>
        </div>
      </div>

      <div className="relative">
        {/* Error overlay with button */}
        {hasError && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-secondary-400/70 backdrop-blur-[50px]">
            <Button
              onPress={() => refetch()}
              variant="outlined"
              width="fit"
              className="text-white shadow-lg"
            >
              Failed to load historical data - click to refetch
            </Button>
          </div>
        )}

        {/* Chart */}
        <ResponsiveContainer
          width="100%"
          className="dd:pl-0 pl-3"
          height={CHART_HEIGHT}
        >
          <ComposedChart
            data={liveData}
            margin={{
              top: 20,
              right: 30,
              left: -15,
              bottom: 5,
            }}
          >
            <defs>
              <linearGradient
                id="percentileGradient"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
                gradientUnits="objectBoundingBox"
              >
                <stop
                  offset="0%"
                  stopColor="#FFF22A"
                />
                <stop
                  offset="100%"
                  stopColor="#FFD001"
                />
              </linearGradient>
            </defs>

            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#AAAAAA"
              opacity={0.3}
              horizontal={true}
              vertical={true}
              horizontalCoordinatesGenerator={(props) => {
                const { height, offset } = props;
                const lines = [];
                const top = offset?.top || 0;
                const bottom = offset?.bottom || 0;

                // Generate horizontal lines but exclude the bottom axis (y = height)
                for (let i = 1; i <= 4; i++) {
                  const y = height - (height / 5) * i;
                  if (y > top && y < height - bottom) {
                    lines.push(y);
                  }
                }
                return lines;
              }}
              verticalCoordinatesGenerator={(props) => {
                const { width, offset } = props;
                const lines = [];
                const left = offset?.left || 0;
                const right = offset?.right || 0;

                // Generate vertical lines but exclude the left axis (x = 0)
                const totalWidth = width - left - right;
                for (let i = 1; i <= 4; i++) {
                  const x = left + (totalWidth / 5) * i;
                  if (x > left && x < width - right) {
                    lines.push(x);
                  }
                }
                return lines;
              }}
            />
            <XAxis
              dataKey="date"
              axisLine={{ stroke: "#AAAAAA", strokeWidth: 0.3 }}
              tickLine={false}
              tick={<CustomXAxisTick />}
              minTickGap={100}
            />
            <YAxis
              axisLine={{ stroke: "#AAAAAA", strokeWidth: 0.3 }}
              tickLine={false}
              tick={<CustomYAxisTick />}
              domain={yAxisDomain}
              tickCount={6}
              interval={0}
            />
            <Tooltip content={<CustomTooltip />} />

            {/* Yellow gradient area for percentile range */}
            <Area
              type="monotone"
              dataKey="baseSalary"
              stroke="none"
              fill="url(#percentileGradient)"
              fillOpacity={0.8}
              name={quantile?.title || t("jobDetails.historicalSalaryTrend.percentileRange")}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
